import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Divider,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useCart } from '../contexts/CartContext';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import StripeProvider from '../components/Checkout/StripeProvider';
import StripePayment from '../components/Checkout/StripePayment';
import ShippingOptions from '../components/Checkout/ShippingOptions';
import DiscountCodeInput from '../components/Checkout/DiscountCodeInput';
import ErrorBoundary from '../components/ErrorBoundary';
import { validateDiscountCode } from '../services/discountService';
import { supabase } from '../lib/supabase';

const EMPTY_SHIPPING = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

// Stripe checkout form component that uses our StripePayment component
const StripeCheckoutForm = ({ onSuccess, shippingOption, shippingData, onPricingLoaded, discountCode }) => {
  const { cart } = useCart();

  // Get shipping cost from the selected shipping option
  const shippingCost = shippingOption ? shippingOption.price : 0;

  // Use cart total as the base amount placeholder (server calculates the real total)
  const amountInCents = Math.round(cart.total * 100);

  // Prepare customer and shipping information
  const customerInfo = shippingData ? {
    firstName: shippingData.firstName,
    lastName: shippingData.lastName,
    email: shippingData.email,
    phone: shippingData.phone
  } : null;
  
  // Prepare shipping information
  const shippingInfo = shippingData ? {
    name: `${shippingData.firstName} ${shippingData.lastName}`,
    address1: shippingData.address1,
    address2: shippingData.address2,
    city: shippingData.city,
    state: shippingData.state,
    zip: shippingData.zip,
    country: shippingData.country || 'US'
  } : null;
  
  // Log customer and shipping info for debugging
  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };
  
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Payment Information
      </Typography>
      
      <StripeProvider>
        <StripePayment
          amount={amountInCents}
          shippingCost={shippingCost}
          shippingOptionId={shippingOption?.id}
          discountCode={discountCode}
          onSuccess={onSuccess}
          onError={handlePaymentError}
          onPricingLoaded={onPricingLoaded}
          customerInfo={customerInfo}
          shippingInfo={shippingInfo}
          orderItems={cart.items}
        />
      </StripeProvider>
    </Box>
  );
};

// Checkout form for shipping and billing information
const CheckoutForm = ({ onSubmit, onShippingOptionChange, initialData = {} }) => {
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    email: initialData.email || '',
    address1: initialData.address1 || '',
    address2: initialData.address2 || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip: initialData.zip || '',
    country: initialData.country || 'US',
    phone: initialData.phone || ''
  });
  
  const [shippingOption, setShippingOption] = useState(null);
  const [formComplete, setFormComplete] = useState(false);
  
  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        address1: initialData.address1 || '',
        address2: initialData.address2 || '',
        city: initialData.city || '',
        state: initialData.state || '',
        zip: initialData.zip || '',
        country: initialData.country || 'US',
        phone: initialData.phone || ''
      });
      }
  }, [initialData]);
  
  // Check if required shipping fields are filled
  useEffect(() => {
    const requiredFields = ['firstName', 'lastName', 'email', 'address1', 'city', 'state', 'zip'];
    const isComplete = requiredFields.every(field => formData[field].trim() !== '');
    setFormComplete(isComplete);
  }, [formData]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleShippingOptionChange = (option) => {
    try {
      setShippingOption(option);
      if (onShippingOptionChange) {
        onShippingOptionChange(option);
      }
    } catch (error) {
      console.error('Error handling shipping option change:', error);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    try {
      // Ensure a shipping option is selected before proceeding
      if (!shippingOption) {
        alert('Please select a shipping option');
        return;
      }
      
      onSubmit({
        ...formData,
        shippingOption
      });
    } catch (error) {
      console.error('Error submitting checkout form:', error);
      alert('An error occurred while submitting the form. Please try again.');
    }
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Shipping Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="firstName"
            name="firstName"
            label="First name"
            fullWidth
            autoComplete="given-name"
            value={formData.firstName}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="lastName"
            name="lastName"
            label="Last name"
            fullWidth
            autoComplete="family-name"
            value={formData.lastName}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="email"
            name="email"
            label="Email"
            fullWidth
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="address1"
            name="address1"
            label="Address line 1"
            fullWidth
            autoComplete="shipping address-line1"
            value={formData.address1}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="address2"
            name="address2"
            label="Address line 2"
            fullWidth
            autoComplete="shipping address-line2"
            value={formData.address2}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="city"
            name="city"
            label="City"
            fullWidth
            autoComplete="shipping address-level2"
            value={formData.city}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="state"
            name="state"
            label="State/Province/Region"
            fullWidth
            value={formData.state}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="zip"
            name="zip"
            label="Zip / Postal code"
            fullWidth
            autoComplete="shipping postal-code"
            value={formData.zip}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="phone"
            name="phone"
            label="Phone Number"
            fullWidth
            autoComplete="tel"
            value={formData.phone}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
      
      {/* Shipping Options Section */}
      {formComplete && (
        <ErrorBoundary>
          <ShippingOptions 
            destination={formData} 
            onShippingChange={handleShippingOptionChange} 
          />
        </ErrorBoundary>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 3, ml: 1 }}
          disabled={!formComplete || !shippingOption}
        >
          Continue to Payment
        </Button>
      </Box>
    </Box>
  );
};

// Order summary component
export const OrderSummary = ({ cart, shippingOption, serverPricing, step, discount, onApplyDiscount, onRemoveDiscount, email }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // The rate-card price of the selected option, before any code is applied.
  const listShippingCents = shippingOption ? Math.round(shippingOption.price * 100) : 0;

  // create-payment-intent silently ignores a code it can no longer honour and
  // charges full price. Once the server has spoken it is the only truth: a
  // reported discount of zero means the code was dropped, so stop showing it
  // rather than leaving a stale saving on screen.
  const serverDiscountCents = serverPricing ? (serverPricing.discountCents || 0) : null;
  const activeDiscount = serverPricing
    ? (serverDiscountCents > 0 ? discount : null)
    : discount;

  // Prefer the server's shipping number once we have it — that's what the card
  // is actually charged, and the client-side rate table could drift from it.
  // Before it arrives, a free-shipping code has already told us the shipping it
  // waives (`shippingCents`), and that has to show here — otherwise the preview
  // charges the shopper for shipping the server will not collect.
  const effectiveShippingCents = serverPricing?.shippingCents != null
    ? serverPricing.shippingCents
    : (activeDiscount?.shippingCents != null && activeDiscount.shippingCents < listShippingCents
      ? activeDiscount.shippingCents
      : listShippingCents);

  const shippingDiscounted = Boolean(shippingOption) && effectiveShippingCents < listShippingCents;

  // The server reports one `discountCents` covering both merchandise and waived
  // shipping. The shipping half is shown on the shipping line, so subtract it
  // here to avoid counting the same saving twice.
  const shippingSavedCents = Math.max(listShippingCents - effectiveShippingCents, 0);
  const merchandiseDiscountCents = serverPricing
    ? Math.max(serverDiscountCents - shippingSavedCents, 0)
    : (activeDiscount?.discountCents || 0);

  const shippingCost = effectiveShippingCents / 100;

  // Use server-calculated tax and total when available, otherwise fall back to 0
  const taxAmount = serverPricing ? serverPricing.taxAmountCents / 100 : 0;
  const orderTotal = serverPricing
    ? serverPricing.totalCents / 100
    : Math.max(cart.total - merchandiseDiscountCents / 100, 0) + shippingCost;

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Order Summary
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ mb: 2 }}>
        {cart.items.map((item) => (
          <Box key={item.cartItemId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              {item.name} x {item.quantity}
              {item.options?.engraving && (
                <Typography variant="caption" display="block" sx={{ ml: 2 }}>
                  Engraving: {item.options.engraving}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2">{formatPrice(item.price * item.quantity)}</Typography>
          </Box>
        ))}
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Subtotal</Typography>
        <Typography variant="body2">{formatPrice(cart.total)}</Typography>
      </Box>

      {activeDiscount && merchandiseDiscountCents > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="success.main">
            Discount ({activeDiscount.code})
          </Typography>
          <Typography variant="body2" color="success.main">
            −{formatPrice(merchandiseDiscountCents / 100)}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Shipping</Typography>
        {shippingOption ? (
          shippingDiscounted ? (
            <Typography variant="body2" color="success.main">
              <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                {formatPrice(listShippingCents / 100)}
              </Box>
              {effectiveShippingCents === 0 ? 'FREE' : formatPrice(shippingCost)}
            </Typography>
          ) : (
            <Typography variant="body2">{formatPrice(shippingCost)}</Typography>
          )
        ) : cart.items.length > 0 ? (
          <Typography variant="body2" color="text.secondary">Select shipping option</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">No items in cart</Typography>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2">Tax</Typography>
        {serverPricing ? (
          <Typography variant="body2">{formatPrice(taxAmount)}</Typography>
        ) : step === 1 ? (
          <Typography variant="body2" color="text.secondary">Calculating...</Typography>
        ) : (
          <Typography variant="body2">{formatPrice(taxAmount)}</Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Total</Typography>
        {serverPricing ? (
          <Typography variant="h6">{formatPrice(orderTotal)}</Typography>
        ) : step === 1 ? (
          <Typography variant="h6" color="text.secondary">Calculating...</Typography>
        ) : (
          <Typography variant="h6">{formatPrice(orderTotal)}</Typography>
        )}
      </Box>

      <DiscountCodeInput
        items={cart.items.map((i) => ({ id: String(i.id), quantity: i.quantity }))}
        shippingOptionId={shippingOption?.id || null}
        shippingCents={listShippingCents}
        email={email}
        applied={activeDiscount}
        onApply={onApplyDiscount}
        onRemove={onRemoveDiscount}
      />
    </Paper>
  );
};

// Main Checkout page component
const Checkout = () => {
  const { cart, clearCart } = useCart();
  const { showSnackbar } = useAppContext();
  const { user, customer, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [serverPricing, setServerPricing] = useState(null);
  // Initialize shippingData with empty values to prevent null errors
  const [shippingData, setShippingData] = useState(EMPTY_SHIPPING);
  const [shippingOption, setShippingOption] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [createAccount, setCreateAccount] = useState(false);

  // Re-check an applied code whenever the cart or shipping changes — a code
  // scoped to an item the shopper just removed must stop showing a discount.
  useEffect(() => {
    if (!discount) return;
    let cancelled = false;
    (async () => {
      const result = await validateDiscountCode({
        code: discount.code,
        items: cart.items.map((i) => ({ id: String(i.id), quantity: i.quantity })),
        shippingOptionId: shippingOption?.id || null,
        shippingCents: shippingOption ? Math.round(shippingOption.price * 100) : 0,
        email: shippingData?.email || null,
      });
      if (cancelled) return;
      setDiscount(result.valid ? result : null);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items, shippingOption]);


  // Check if cart is empty and redirect to products if it is
  useEffect(() => {
    if (cart.items.length === 0 && !orderComplete) {
      navigate('/products');
      showSnackbar('Your cart is empty', 'info');
    }
  }, [cart.items.length, navigate, showSnackbar, orderComplete]);
  
  // Pre-fill shipping data for signed-in customers.
  //
  // `user` is the Supabase auth user (id/email/user_metadata) — it has no
  // firstName/lastName/addresses. The name lives on the `customers` row and
  // the address in `customer_addresses`, so both have to be read explicitly;
  // reading them off `user` silently prefilled nothing.
  useEffect(() => {
    let cancelled = false;

    const prefill = async () => {
      if (!isAuthenticated || !user) {
        setShippingData(EMPTY_SHIPPING);
        return;
      }

      const meta = user.user_metadata || {};
      const base = {
        ...EMPTY_SHIPPING,
        firstName: customer?.first_name || meta.given_name || '',
        lastName: customer?.last_name || meta.family_name || '',
        email: customer?.email || user.email || '',
        phone: customer?.phone || '',
      };

      if (!customer?.id) {
        if (!cancelled) setShippingData(base);
        return;
      }

      try {
        const { data: addresses } = await supabase
          .from('customer_addresses')
          .select('*')
          .eq('customer_id', customer.id)
          .order('is_default', { ascending: false })
          .limit(1);

        const addr = addresses?.[0];
        if (cancelled) return;
        setShippingData(addr
          ? {
              ...base,
              firstName: base.firstName || addr.first_name || '',
              lastName: base.lastName || addr.last_name || '',
              phone: base.phone || addr.phone || '',
              address1: addr.address1 || '',
              address2: addr.address2 || '',
              city: addr.city || '',
              state: addr.state || '',
              zip: addr.zip || '',
              country: addr.country || 'US',
            }
          : base);
      } catch (error) {
        console.error('Error pre-filling shipping data:', error);
        if (!cancelled) setShippingData(base);
      }
    };

    prefill();
    return () => { cancelled = true; };
  }, [isAuthenticated, user, customer]);
  
  const steps = ['Shipping', 'Payment', 'Confirmation'];
  
  const handleShippingSubmit = (data) => {
    setShippingData(data);
    // Make sure we capture the shipping option from the form data
    if (data.shippingOption) {
      setShippingOption(data.shippingOption);
    }
    setActiveStep(1);
  };
  
  const handleShippingOptionChange = (option) => {
    setShippingOption(option);
  };
  
  const handlePaymentSuccess = (paymentIntentId) => {
    // Get the order ID from localStorage (set by the StripePayment component)
    const savedOrderId = localStorage.getItem('currentOrderId');
    
    // Use the saved order ID or generate a new one if not available
    const newOrderId = savedOrderId || `KB-${Date.now().toString().slice(-6)}`;
    
    setOrderId(newOrderId);
    setOrderComplete(true);
    setActiveStep(2);
    clearCart();
    
    // Reset shipping data after order completion
    setShippingData(null);
    setShippingOption(null);
    
    // Clear the current order ID from localStorage
    localStorage.removeItem('currentOrderId');
  };
  
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <ErrorBoundary>
          {!isAuthenticated && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Already have an account? <Link 
                  to="/login" 
                  state={{ from: '/checkout', returnTo: '/checkout' }}
                  style={{ color: '#8B4513' }}
                >
                  Login
                </Link> to speed up checkout.
              </Alert>
            </Box>
          )}
          <CheckoutForm 
            onSubmit={handleShippingSubmit} 
            onShippingOptionChange={handleShippingOptionChange}
            initialData={shippingData} 
          />
        </ErrorBoundary>;
      case 1:
        return <ErrorBoundary>
          <StripeCheckoutForm
            onSuccess={handlePaymentSuccess}
            shippingOption={shippingOption}
            shippingData={shippingData}
            onPricingLoaded={(pricing) => setServerPricing(pricing)}
            discountCode={discount?.code}
          />
        </ErrorBoundary>;
      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Thank you for your order!
            </Typography>
            <Typography variant="subtitle1">
              Your order number is #{orderId}. We have emailed your order
              confirmation, and will send you an update when your order has
              shipped.
            </Typography>
            
            {!isAuthenticated && (
              <Box sx={{ mt: 4, mb: 3, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Create an account to track your orders
                </Typography>
                <Typography variant="body2" paragraph>
                  Would you like to create an account to easily track your orders and manage your shipping addresses?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createAccount}
                      onChange={(e) => setCreateAccount(e.target.checked)}
                      name="createAccount"
                      color="primary"
                    />
                  }
                  label="Yes, create an account with my email"
                />
                {createAccount && (
                  <Button
                    variant="outlined"
                    sx={{ 
                      mt: 2, 
                      color: '#8B4513',
                      borderColor: '#8B4513',
                      '&:hover': {
                        borderColor: '#6d3610',
                      }
                    }}
                    onClick={() => navigate('/register', { state: { email: shippingData?.email } })}
                  >
                    Create Account
                  </Button>
                )}
              </Box>
            )}
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/products')}
              sx={{ mt: 3 }}
            >
              Continue Shopping
            </Button>
          </Box>
        );
      default:
        throw new Error('Unknown step');
    }
  };
  
  // Handle potential errors in the checkout process
  if (!cart && !orderComplete) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Unable to load cart information. Please try refreshing the page.
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Checkout
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {getStepContent(activeStep)}
          </Grid>
          <Grid item xs={12} md={4}>
            <ErrorBoundary>
              <OrderSummary
                cart={cart}
                shippingOption={shippingOption}
                serverPricing={serverPricing}
                step={activeStep}
                discount={discount}
                email={shippingData?.email}
                onApplyDiscount={setDiscount}
                onRemoveDiscount={() => setDiscount(null)}
              />
              
              {activeStep === 0 && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/products')}
                    fullWidth
                  >
                    Continue Shopping
                  </Button>
                </Box>
              )}
            </ErrorBoundary>
          </Grid>
        </Grid>
      </Container>
    </ErrorBoundary>
  );
};

export default Checkout;
